import type { FileUIPart, SourceDocumentUIPart } from "ai";
import { FileTextIcon, PaperclipIcon, XIcon } from "lucide-react";
import type { HTMLAttributes } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";

export type AttachmentData = FileUIPart | SourceDocumentUIPart;

function attachmentName(data: AttachmentData): string {
	return data.type === "source-document"
		? data.filename || data.title
		: data.filename || (data.mediaType.startsWith("image/") ? "Image" : "Attachment");
}

function imageUrl(data: AttachmentData): string | null {
	return data.type === "file" && data.mediaType.startsWith("image/") && data.url
		? data.url
		: null;
}

export type AttachmentProps = HTMLAttributes<HTMLDivElement> & {
	data: AttachmentData;
	variant?: "compact" | "preview";
	onRemove?: () => void;
};

/** Shared AI Elements attachment presentation for composer and transcript. */
export function Attachment({
	data,
	variant = "preview",
	className,
	onRemove,
	...props
}: AttachmentProps) {
	const name = attachmentName(data);
	const image = imageUrl(data);
	const compact = variant === "compact";
	return (
		<div
			className={cn(
				"group relative overflow-hidden rounded-lg border border-border bg-background",
				compact
					? "flex h-8 max-w-64 items-center gap-1.5 px-1.5 font-medium text-sm hover:bg-accent"
					: "size-24",
				className,
			)}
			{...props}
		>
			<div
				className={cn(
					"flex shrink-0 items-center justify-center overflow-hidden bg-muted text-muted-foreground",
					compact ? "size-5 rounded" : "size-full",
				)}
			>
				{image === null ? (
					data.type === "source-document" ? (
						<FileTextIcon className={compact ? "size-3" : "size-4"} />
					) : (
						<PaperclipIcon className={compact ? "size-3" : "size-4"} />
					)
				) : (
					<img
						alt={name}
						className="size-full object-cover"
						decoding="async"
						loading="lazy"
						src={image}
					/>
				)}
			</div>
			{compact && <span className="min-w-0 flex-1 truncate">{name}</span>}
			{onRemove && (
				<Button
					aria-label={`Remove ${name}`}
					className={cn(
						"size-5 rounded-full p-0 can-hover:opacity-0 transition-opacity group-hover:opacity-100 [&>svg]:size-2.5",
						compact ? "shrink-0" : "absolute top-2 right-2 bg-background/80 backdrop-blur-sm",
					)}
					onClick={(event) => {
						event.stopPropagation();
						onRemove();
					}}
					type="button"
					variant="ghost"
				>
					<XIcon />
				</Button>
			)}
			{!compact && image === null && (
				<span className="sr-only">{name}</span>
			)}
		</div>
	);
}

export type AttachmentsProps = HTMLAttributes<HTMLDivElement>;

export function Attachments({ children, className, ...props }: AttachmentsProps) {
	if (!children) return null;
	return (
		<div className={cn("flex flex-wrap items-start gap-2", className)} {...props}>
			{children}
		</div>
	);
}
